import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Phone, Mail, Clock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContactChannel } from '@/hooks/useContactChannel';

/**
 * Fallback Live Chat Widget Component
 * 
 * This is a fallback WhatsApp-based chat widget that displays when Tawk.to 
 * is not yet configured or unavailable. It provides quick access to WhatsApp
 * and contact options.
 * 
 * Once Tawk.to is properly configured with actual account credentials,
 * the TawkWidget component handles the primary live chat functionality.
 */

const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [tawkLoaded, setTawkLoaded] = useState(false);
  const channel = useContactChannel();

  // Check if Tawk.to widget is loaded
  useEffect(() => {
    const checkTawk = setInterval(() => {
      if (window.Tawk_API && typeof window.Tawk_API.showWidget === 'function') {
        setTawkLoaded(true);
        clearInterval(checkTawk);
      }
    }, 1000);

    // Stop checking after 10 seconds
    setTimeout(() => clearInterval(checkTawk), 10000);

    return () => clearInterval(checkTawk);
  }, []);

  // Don't render this widget if Tawk.to is already loaded
  if (tawkLoaded) {
    return null;
  }

  const quickReplies = [
    'I need help with Canada PR',
    'Australia visa inquiry',
    'Gulf job opportunities',
    'Saudi Arabia visa processing',
    'Free profile assessment',
  ];

  const openWhatsApp = (customMessage = '') => {
    const text = customMessage || message || channel.whatsappGreeting || 'Hello Zafco';
    window.open(channel.whatsappUrlWith(text), '_blank', 'noopener,noreferrer');
    setMessage('');
  };

  const handleQuickReply = (reply) => {
    openWhatsApp(`Hi Zafco, ${reply}`);
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="whatsapp-chat-button"
        aria-label="Open WhatsApp chat"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              className="relative"
            >
              <MessageCircle className="w-6 h-6" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-80 md:w-96 bg-white rounded-2xl shadow-2xl overflow-hidden"
            data-testid="whatsapp-chat-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Chat with Zafco</h3>
                  <div className="flex items-center gap-1 text-sm text-green-100">
                    <span className="w-2 h-2 rounded-full bg-green-300 animate-pulse" />
                    <span>Online now</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Body */}
            <div className="p-4 bg-gray-50 min-h-[200px]">
              {/* Welcome Message */}
              <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
                <p className="text-gray-700 text-sm">
                  👋 <strong>Welcome to Zafco!</strong>
                </p>
                <p className="text-gray-600 text-sm mt-2">
                  We're a Government of India approved recruitment agency. How can we help you today?
                </p>
              </div>

              {/* Quick Replies */}
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Quick Options:</p>
                {quickReplies.map((reply, index) => (
                  <motion.button
                    key={index}
                    onClick={() => handleQuickReply(reply)}
                    className="w-full text-left px-4 py-2 bg-white rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-green-50 hover:border-green-300 transition-colors"
                    whileHover={{ x: 5 }}
                    data-testid={`quick-reply-${index}`}
                  >
                    {reply}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Input Area */}
            <div className="p-4 border-t bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && openWhatsApp()}
                  data-testid="chat-message-input"
                />
                <Button 
                  onClick={() => openWhatsApp()}
                  className="bg-green-500 hover:bg-green-600 px-4"
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mt-2">
                <ExternalLink className="w-3 h-3" />
                <span>Opens in WhatsApp</span>
              </div>
            </div>

            {/* Contact Info Footer */}
            <div className="px-4 py-3 bg-gray-100 border-t flex items-center justify-around text-xs text-gray-600">
              <a href={channel.phoneTelUrl} className="flex items-center gap-1 hover:text-green-600" data-testid="livechat-call" data-vertical={channel.vertical}>
                <Phone className="w-3 h-3" />
                Call Us
              </a>
              <a href={`mailto:${channel.email}`} className="flex items-center gap-1 hover:text-green-600">
                <Mail className="w-3 h-3" />
                Email
              </a>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Mon-Sat 9-6
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LiveChatWidget;
